/**
 * CHOICE PROPOSITIONS
 * Pure rule : the answers of a multiple choice question (QCM) can neither be empty nor identical
 */

interface ChoiceQuestion {
  type: string
  draft?: boolean
  choiceCount?: number | null //Maximum number of answers the participant can tick (null : one answer)
  Propositions?: { id: string; name?: { [language: string]: string } }[]
}

function normalize(label: string): string {
  return label.normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase()
}

//Answers without text in every language (empty in a single language is an untranslated answer, handled elsewhere)
//and answers identical inside a language. Draft questions are not part of the questionnaire yet.
export function getChoicePropositionIssues(
  question: ChoiceQuestion,
  languages: string[]
): { emptyCount: number; duplicates: { language: string; label: string }[] } {
  const result = { emptyCount: 0, duplicates: [] as { language: string; label: string }[] }
  if (question.type !== "choice" || question.draft) return result

  const propositions = question.Propositions || []
  const labelOf = (proposition, language: string): string => normalize(proposition.name?.[language] || "")

  result.emptyCount = propositions.filter((p) => languages.every((language) => !labelOf(p, language))).length

  languages.forEach((language) => {
    const seen: { [normalized: string]: boolean } = {}
    for (const proposition of propositions) {
      const normalized = labelOf(proposition, language)
      if (!normalized) continue
      if (seen[normalized]) {
        result.duplicates.push({ language, label: (proposition.name?.[language] || "").trim() })
        break
      }
      seen[normalized] = true
    }
  })

  return result
}

//A QCM needs at least two answers, and more answers than the maximum the participant can tick (otherwise there is no choice)
export function getChoiceTooFewPropositions(question: ChoiceQuestion): { count: number; limit: number | null } | null {
  if (question.type !== "choice" || question.draft) return null
  const count = (question.Propositions || []).length
  const limit = question.choiceCount && question.choiceCount > 1 ? question.choiceCount : null
  return count < 2 || (limit !== null && count <= limit) ? { count, limit } : null
}
